import { Dispatch } from 'redux';
import { client } from '../../utils/apollo';
import { FetchResult } from 'react-apollo';
import { ApolloQueryResult, OperationVariables, DocumentNode } from 'apollo-boost';
import { getGraphQLErrorMessage } from '../../utils/formHelpers';

import { setRequestError, setRequestState, setRecommendations } from '../actions/request';
import { GET_ACCOUNT } from '../../graphql/queries';
import { CreateEnquiryInput, CreateLeadInput } from '../../../__generated__/globalTypes';
import { CREATE_ENQUIRY, CREATE_LEAD } from '../../graphql/mutations';
import {
  GetAccount,
  GetAccount_viewer_account
} from '../../graphql/queries/__generated__/GetAccount';

export type CalculateSavingsParams = {
  query: DocumentNode;
  options: OperationVariables;
  submit: boolean;
  validate: boolean;
};

export type CheckPostcodeParams = {
  query: DocumentNode;
  options: OperationVariables;
  submit: boolean;
  validate: boolean;
};

export const submitSolarForm = (input: CreateLeadInput) => async (
  dispatch: Dispatch
): Promise<FetchResult<{}, Record<string, any>> | undefined> => {
  try {
    dispatch(setRequestState({ submitting: true, validating: false }));
    const data = await client.mutate<{}, OperationVariables>({
      mutation: CREATE_LEAD,
      variables: {
        input
      },
      fetchPolicy: 'no-cache'
    });
    dispatch(setRequestState({ submitting: false, error: false }));
    return Promise.resolve(data);
  } catch (error) {
    const { graphQLErrors } = error;
    const message = getGraphQLErrorMessage(graphQLErrors, input);
    dispatch(setRequestError({ error: true, message }));
    return Promise.reject(error);
  }
};

export const submitCreateEnquiry = (input: CreateEnquiryInput) => async (
  dispatch: Dispatch
): Promise<FetchResult<{}, Record<string, any>> | undefined> => {
  try {
    dispatch(setRequestState({ submitting: true, validating: false }));
    const data = await client.mutate<{}, OperationVariables>({
      mutation: CREATE_ENQUIRY,
      variables: {
        input
      },
      fetchPolicy: 'no-cache'
    });
    dispatch(setRequestState({ submitting: false, error: false }));
    return Promise.resolve(data);
  } catch (error) {
    const { graphQLErrors } = error;
    const message = getGraphQLErrorMessage(graphQLErrors, input);
    dispatch(setRequestError({ error: true, message }));
    return Promise.reject(error);
  }
};

export const dispatchSetRecommendations = (dispatch: Dispatch, { data }) => {
  dispatch(setRecommendations(data.calculateSolarRecommendations));
};

export const queryGraphQL = <T>(params: CalculateSavingsParams | CheckPostcodeParams) => async (
  dispatch: Dispatch
): Promise<ApolloQueryResult<T>> => {
  const { query, options, submit, validate } = params;
  try {
    dispatch(setRequestState({ submitting: submit, validating: validate }));
    const data = await client.query<T, OperationVariables>({
      query,
      variables: {
        options
      },
      fetchPolicy: 'network-only'
    });
    dispatch(setRequestState({ submitting: false, validating: false, error: false }));
    dispatchSetRecommendations(dispatch, data);
    return Promise.resolve(data);
  } catch (error) {
    dispatch(setRequestError({ error: true, message: undefined }));
    return Promise.reject(error);
  }
};

export const calculateSolarSavings = (query: DocumentNode, options: OperationVariables) => {
  const params: CalculateSavingsParams = {
    query,
    options,
    submit: true,
    validate: false
  };
  return queryGraphQL(params);
};

export const checkIfPostcodeServiceable = (query: DocumentNode, options: OperationVariables) => {
  const params: CheckPostcodeParams = {
    query,
    options,
    submit: false,
    validate: true
  };
  return queryGraphQL(params);
};

export const getAccount = () => async (dispatch: Dispatch): Promise<GetAccount_viewer_account> => {
  try {
    dispatch(setRequestState({ submitting: true, validating: false }));
    const result = await client.query<GetAccount, OperationVariables>({
      query: GET_ACCOUNT,
      variables: {
        options: {}
      },
      fetchPolicy: 'network-only'
    });
    let account = undefined;
    if (result && result.data && result.data.viewer && result.data.viewer.account) {
      account = result.data.viewer.account;
    }

    dispatch(setRequestState({ submitting: false, validating: false, error: false }));
    return account;
  } catch (error) {
    dispatch(setRequestError({ error: true, message: undefined }));
    return Promise.reject(error);
  }
};
